import type { Configuration } from '@/config/types'
import type { CeLoginApiClient } from '@/scopes/account/sunweb-ce/infrastructure/ceLoginApiClient'
import type { AnalyticsClient, CookieClient } from '@/scopes/common/domain/types'
import type { QueryStringClient } from '../../infrastructure/query-strings/queryStringClient'
import { createLoggedUserEvent } from '@/domain/header/analytics'
import { AbTestData } from '@/scopes/common/infrastructure/ab-testing/edge-function/types'
import { CommonAnalyticsClient } from '@/scopes/common/infrastructure/analytics/ga4CommonAnalyticsClient'
import { parseUserCookie } from './parseUserCookie'

interface Dependencies {
  analyticsClient: AnalyticsClient
  commonAnalyticsClient: CommonAnalyticsClient
  cookieClient: CookieClient
  queryStringClient: QueryStringClient
  config: Configuration
  apiClient: CeLoginApiClient
}

interface ReactPageLoadActionPayload {
  abTest: AbTestData
}

export default function useCases({
  analyticsClient,
  commonAnalyticsClient,
  cookieClient,
  queryStringClient,
  config,
  apiClient,
}: Dependencies) {
  async function reactPageLoaded({
    abTest,
  }: ReactPageLoadActionPayload) {
    const didLogin = await loginSunwebCEUser()
    emitLoggedUserEvent()
    sendABTestVariantValueEvent(
      { abTest },
      { cookieClient, commonAnalyticsClient },
    )

    return didLogin
  }

  function emitLoggedUserEvent() {
    const userData = cookieClient.get('user-data')
    const { userId, isLoggedIn } = parseUserCookie(userData)
    analyticsClient.sendEvent(createLoggedUserEvent({ loggedIn: isLoggedIn, userId }))
  }

  async function loginSunwebCEUser() {
    const siteName = config.siteName
    if (siteName !== 'sunwebfr')
      return false
    const user = queryStringClient.getValue('usr')
    const pass = queryStringClient.getValue('pas')
    queryStringClient.remove('usr')
    queryStringClient.remove('pas')

    if (!user || !pass)
      return false

    const { isValidCeUser } = await apiClient.login(user, pass)
    return isValidCeUser ?? false
  }

  return {
    reactPageLoaded,
    emitLoggedUserEvent,
    loginSunwebCEUser,
  }
}

function sendABTestVariantValueEvent(
  payload: { abTest: AbTestData },
  dependencies: { cookieClient: CookieClient, commonAnalyticsClient: CommonAnalyticsClient },
) {
  const { abTest } = payload
  const { cookieClient, commonAnalyticsClient } = dependencies

  const aBTestCookieValue = cookieClient.get('sunweb_ab')

  if (aBTestCookieValue) {
    commonAnalyticsClient.sendAbVariantEvent(abTest)
  }
}
